/**
 * useEscapeClose — Wires up `Esc` key to close a modal/sheet.
 *
 * Used uniformly across all redesigned modals so keyboard users get the
 * same dismissal behavior everywhere. Skips if focus is in a typing target
 * so multi-line text inputs (with their own Esc bindings) take precedence.
 */

import { useEffect } from 'react';

export function useEscapeClose(onClose: () => void, enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) return;

    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      // Allow Esc to close even from inputs in modal forms — we want users
      // to bail out of a half-typed form. The reviewer skips inputs because
      // *typing* shouldn't trigger reviewer actions; here it's the opposite.
      return false;
    }

    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      onClose();
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, enabled]);
}
